import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { MediaComponent } from '@klickdata/core/media/src/media.component';
import { MobileService, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, Resource, ResourceTag } from '@klickdata/core/resource';
import { ResourceItem, ResourceItemService, ResourceItemTypes } from '@klickdata/core/resource-item';
import { TagService } from '@klickdata/core/resource/src/tag/tag.service';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    shareReplay,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { ResourceListingSheetComponent } from '../../resource-listing-sheet/resource-listing-sheet.component';
import { ResourceBuilderService } from '../resource-builder.service';
import { ResourceItemDirective } from './resource-item.directive';
import { Media } from '@klickdata/core/media/src/media.model';
import { ResourceService } from '@klickdata/core/resource/src/resource.service';

@Component({
    selector: 'app-resource-item-form',
    templateUrl: './resource-item-form.component.html',
    styleUrls: ['./resource-item-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceItemFormComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(MatExpansionPanel) questionExtrasPanel: MatExpansionPanel;
    @ViewChild('title') titleInput: ElementRef;
    @ViewChild('itemDirective') itemDirective: ResourceItemDirective;
    @Input()
    public item: ResourceItem;

    @Input()
    public itemGroup: FormGroup;

    @Input()
    public usePoints = true;
    @Input()
    public index: number;
    @Output() onSubmitThenRoute: EventEmitter<Resource> = new EventEmitter<Resource>();
    public editable: boolean;
    public ItemTypes = ResourceItemTypes;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public displayDesc = false;
    public questionLoaded: boolean;
    public selection: number[] = [];
    public selectedResource$: Observable<Resource>;
    AppScope = AppScope;
    @ViewChild('app_media') mediaCompRef: MediaComponent;
    public resourceTags$: Observable<ResourceTag[]>;
    public displayedMaterialResourceItem$: BehaviorSubject<ResourceItem> = new BehaviorSubject<ResourceItem>(null);
    public resourceItems$: Observable<ResourceItem[]>;
    public trailerFormControl: FormControl = new FormControl(null);
    public articleCode: FormControl = new FormControl('');
    public otherInfoFormControl: FormControl = new FormControl('');
    constructor(
        protected builderService: ResourceBuilderService,
        protected changeRef: ChangeDetectorRef,
        protected auth: AuthService,
        protected tagService: TagService,
        protected bottomSheet: MatBottomSheet,
        protected itemService: ResourceItemService,
        protected mobileService: MobileService,
        protected router: Router,
        protected resourceService: ResourceService
    ) {}

    ngOnInit() {
        this.itemGroup
            .get('question')
            .valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy))
            .subscribe((question) => {
                this.questionLoaded = !!question;
                this.checkQuestionEditMode();
                this.changeRef.markForCheck();
            });

        combineLatest([this.auth.getUser().pipe(first()), this.builderService.getResource()])
            .pipe(
                map(([user, resource]) => (resource ? user.canEdit(resource) : true)),
                takeUntil(this.destroy)
            )
            .subscribe((editable) => {
                this.editable = editable;
            });

        this.builderService
            .getItems()
            .pipe(takeUntil(this.destroy))
            .subscribe((items) => (this.selection = items.map((item) => item.child_resource_id)));

        this.resourceItems$ = this.itemService
            .getResourceItems(this.itemGroup.value.child_resource_id)
            .pipe(shareReplay());

        // this.builderService
        //     .onSubmit()
        //     .pipe(
        //         takeUntil(this.destroy),
        //         filter((_) => !!this.reouteAfterSubmit)
        //     )
        //     .subscribe((_) => {
        //         this.router.navigate(['/admin/content/courses', this.reouteAfterSubmit.id]);
        //     });
    }

    ngAfterViewInit() {
        this.titleInput?.nativeElement?.focus();
    }
    onResourceExist(resource: Resource) {
        this.updateItemGroup(resource);
        this.resourceTags$ = !resource.tags_attached
            ? EMPTY
            : !!resource.tags?.length
            ? of(resource.tags.map((tag) => new ResourceTag(tag)))
            : this.tagService.getResourceTags(resource.id);
        this.selectedResource$ = of(resource).pipe(
            shareReplay(),
            tap((resource) => {
                this.trailerFormControl.patchValue(resource.medias?.trailer ? resource.medias?.trailer[0] : null);
                this.trailerFormControl.markAsPristine();
                this.articleCode.patchValue(resource.article_code);
                this.articleCode.markAsPristine();
                this.otherInfoFormControl.patchValue(resource.other_info);
                this.otherInfoFormControl.markAsPristine();
            })
        );
        this.trackOtherInfoChanges(resource);
    }

    trackOtherInfoChanges(resource: Resource) {
        this.otherInfoFormControl.valueChanges
            .pipe(
                takeUntil(this.destroy),
                debounceTime(300),
                filter((value) => !!resource?.id && !!value && value !== resource.other_info),
                distinctUntilChanged(),
                switchMap((value) => this.resourceService.update({ id: resource.id, other_info: value }, true))
            )
            .subscribe(() => {
                this.changeRef.markForCheck();
            });
    }

    onTrailerUploaded(media: Media, resource: Resource) {
        if (!!resource?.id && !!media?.id) {
            this.resourceService
                .update({ id: resource.id, medias: { trailer: [Number(media.id)] } }, true)
                .pipe(takeUntil(this.destroy))
                .subscribe(() => {
                    this.changeRef.markForCheck();
                });
        }
    }
    onArticleCodeUpdated(code: string, resource: Resource) {
        setTimeout(() => {
            if (!!resource?.id && !!code && code !== resource.article_code) {
                this.resourceService
                    .update({ id: resource.id, article_code: code }, true)
                    .pipe(takeUntil(this.destroy))
                    .subscribe(() => {
                        this.changeRef.markForCheck();
                    });
            }
        }, 300);
    }
    onTrailerCleared(resource: Resource) {
        if (!!resource?.id) {
            this.resourceService
                .update({ id: resource.id, medias: { trailer: null } }, true)
                .pipe(takeUntil(this.destroy))
                .subscribe(() => {
                    this.changeRef.markForCheck();
                });
        }
    }

    checkQuestionEditMode() {
        if (!this.editable) {
            this.itemGroup.controls.name.disable();
            const question = <FormGroup>this.itemGroup.get('question');
            question.controls.description.disable({ emitEvent: false });
            question.controls.learning_field.disable({ emitEvent: false });
            question.controls.references.disable({ emitEvent: false });
            question.controls.question_type_value.disable({ emitEvent: false });
            question.controls.other_alternative?.disable({ emitEvent: false });
            question.controls.randomize_alternatives.disable({ emitEvent: false });
        }
    }

    updateItemGroup(resource: Resource): void {
        this.itemGroup.patchValue({
            // title: resource.title,
            description: resource.description,
            bullets: resource.bullets,
        });
        if (this.itemGroup.value.item_type_value === ResourceItemTypes.MATERIAL) {
            this.itemGroup.patchValue({
                instructions: resource.instructions,
            });
        }
    }

    updateItemFromResource(resource: Resource, field: string) {
        if (field === 'title') {
            this.itemGroup.get('title').patchValue(resource.title);
        } else if (field === 'bullets') {
            this.itemGroup.get('bullets').patchValue(resource.bullets);
        }
        this.itemGroup.markAsDirty();
        this.itemGroup.updateValueAndValidity();
        this.changeRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    public showTagResources(tag: ResourceTag) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: tag.id,
                label: tag.name,
                scope_id: AppScope.TAGS,
            },
            panelClass: 'sheet-wrapper',
        });
    }
    public removeImage(): void {
        this.item.media_id = null;
        this.item.media.url = null;
        this.itemGroup.get('question').patchValue({
            img_url: null,
            media_id: null,
        });
        FormHelper.markForm(this.itemGroup);
    }

    public toggleDisplayDesc() {
        this.displayDesc = !this.displayDesc;
    }

    public getQsExtraInfo(question: AbstractControl) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.QUESTION_EXTRA_INFO,
            data: {
                type: this.index,
                value: question.value,
                editable: this.editable,
            },
        });

        this.mobileService
            .getSideNavResponseData()
            .pipe(
                filter((data) => data.type === this.index),
                takeUntil(this.destroy),
                map((data) => data.info)
            )
            .subscribe((info) => {
                if (info) {
                    question.get('description')?.patchValue(info.description);
                    question.get('learning_field')?.patchValue(info.learning_field);
                    question.get('references')?.patchValue(info.references);
                    FormHelper.markForm(<FormGroup>question);
                    FormHelper.markForm(this.itemGroup);
                    this.changeRef.markForCheck();
                }
            });
    }
}
